import { Elements } from '@stripe/react-stripe-js';
import { useEffect, useRef } from 'react';
import App from 'next/app';

import { TwitterPixel } from '@playbooks/molecules/pixels';
import { FocusModality } from '@playbooks/ui/components/focus-modality';
import { PageHead } from '@playbooks/ui/components/page-head';
import { DndProvider, HTML5Backend } from '@playbooks/wrappers/dnd';

import 'styles/_base.css';
import 'initializers/icons';
import { BaseAdapter } from 'api';
import {
	AppProvider,
	ClientProvider,
	FathomProvider,
	IntercomProvider,
	LaunchProvider,
	LoadingProvider,
	ModalProvider,
	SearchProvider,
	SessionProvider,
	StorageProvider,
	StoreProvider,
	StripeProvider,
	ThemeProvider,
	ToastProvider,
	WorkspaceProvider,
} from 'contexts';
import { PageContext, PageProvider } from 'contexts/page-context';
import { seo } from 'initializers';
import { sessionQuery } from 'queries';
import { hasAuthCookie, logger, redirectToLogin, redirectToPage } from 'utils';

const client = new BaseAdapter({ domain: process.env.NEXT_PUBLIC_API_DOMAIN });
const authGate = process.env.NEXT_PUBLIC_AUTH_GATE === 'true';
const publicPaths = [
	'/',
	'/403',
	'/404',
	'/500',
	'/auth',
	'/auth/forgot-password',
	'/auth/reset-password',
	'/auth/verify',
	'/errors/sandbox',
	'/errors/runtime',
	'/_error',
	'/forgot-password',
	'/legal',
	'/mkt/legal',
	'/mkt/preferences',
	'/mkt/privacy',
	'/mkt/terms',
	'/mkt/unsubscribe',
	'/preferences',
	'/privacy',
	'/reset-password',
	'/terms',
	'/unsubscribe',
	'/verify',
];

const isPublicPath = pathname => {
	return (
		publicPaths.includes(pathname) ||
		pathname === '/[workspaceId]' ||
		pathname.startsWith('/[workspaceId]/') ||
		['/blog', '/docs', '/oauth'].some(path => pathname === path || pathname.startsWith(`${path}/`))
	);
};

const parseCookies = value => {
	return Object.fromEntries(
		(value || '')
			.split(';')
			.filter(Boolean)
			.map(cookie => cookie.trim().split('='))
			.map(([key, ...parts]) => [key, parts.join('=')]),
	);
};

const expiredCookie = (key, domain) => {
	return `${key}=; Max-Age=0; Path=/;${domain ? ` Domain=${domain};` : ''} Secure; SameSite=Lax`;
};

const expireAuthCookies = ctx => {
	const cookies = [
		expiredCookie('workspace', process.env.NEXT_PUBLIC_DOMAIN),
		expiredCookie('auth', process.env.NEXT_PUBLIC_WILDCARD),
		expiredCookie('tempAuth', process.env.NEXT_PUBLIC_WILDCARD),
		expiredCookie('tempWorkspace', process.env.NEXT_PUBLIC_WILDCARD),
	];
	if (ctx.res) {
		const current = ctx.res.getHeader('Set-Cookie');
		const existing = current ? (Array.isArray(current) ? current : [String(current)]) : [];
		ctx.res.setHeader('Set-Cookie', [...existing, ...cookies]);
	} else if (typeof document !== 'undefined') {
		cookies.forEach(cookie => (document.cookie = cookie));
	}
};

const Playbooks = ({ Component, pageProps, session }) => {
	const sessionRef = useRef(session);
	if (session) sessionRef.current = session;
	const ssr = { ...pageProps, session: session || sessionRef.current };
	const getLayout = Component.getLayout || (page => page);

	// Hooks
	useEffect(() => {
		const formattedData = {};
		Object.keys(ssr)
			.filter(key => key !== '_sentryBaggage')
			.filter(key => key !== '_sentryTraceData')
			.map(key => (formattedData[key] = ssr[key]));
		logger.info('env: ', process.env.NEXT_PUBLIC_ENV);
		logger.info('ssr: ', formattedData);
	}, [ssr]);

	// Render
	return (
		<>
			<FocusModality />
			<PageHead seo={seo} />
			<ToastProvider>
				<StorageProvider>
					<StoreProvider client={client}>
						<SessionProvider ssr={ssr}>
							<ClientProvider client='web'>
								<WorkspaceProvider ssr={ssr}>
									<ThemeProvider>
										<IntercomProvider>
											<LoadingProvider>
												<FathomProvider>
													<SearchProvider>
														<AppProvider ssr={ssr}>
															<StripeProvider Elements={Elements}>
																<ModalProvider>
																	<PageProvider ssr={ssr}>
																		<PageContext.Consumer>
																			{contexts => (
																				<LaunchProvider {...contexts}>
																					<DndProvider backend={HTML5Backend}>
																						{getLayout(<Component ssr={ssr} {...contexts} />, ssr)}
																					</DndProvider>
																				</LaunchProvider>
																			)}
																		</PageContext.Consumer>
																	</PageProvider>
																</ModalProvider>
															</StripeProvider>
														</AppProvider>
													</SearchProvider>
												</FathomProvider>
											</LoadingProvider>
										</IntercomProvider>
									</ThemeProvider>
								</WorkspaceProvider>
							</ClientProvider>
						</SessionProvider>
					</StoreProvider>
				</StorageProvider>
			</ToastProvider>
			<TwitterPixel />
		</>
	);
};

Playbooks.getInitialProps = async appContext => {
	const appProps = await App.getInitialProps(appContext);
	const { req, params, pathname, query } = appContext.ctx;
	const cookies = req?.cookies || (typeof document !== 'undefined' ? parseCookies(document.cookie) : {});
	const publicPath = isPublicPath(pathname);
	try {
		if (!hasAuthCookie(cookies)) {
			if (!publicPath) redirectToLogin(appContext.ctx);
			return appProps;
		}
		const session = await sessionQuery({ req: req || { cookies }, params, query });
		if (authGate && !session.data?.isAdmin) {
			expireAuthCookies(appContext.ctx);
			if (!publicPath) redirectToPage(appContext.ctx, '/?view=splash');
			return appProps;
		}
		return { ...appProps, session };
	} catch (e) {
		if (authGate) {
			expireAuthCookies(appContext.ctx);
			if (!publicPath) redirectToPage(appContext.ctx, '/?view=splash');
			return appProps;
		}
		if (e.status === 401) redirectToLogin(appContext.ctx);
		return appProps;
	}
};

export default Playbooks;
